import { ChevronLeft } from "@/components/ui/icons";
import { Pressable, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

export type PublicDetailHeaderProps = {
  /** The card's product name, echoed instead of a generic "Item detail" (#469). */
  title: string;
  /**
   * Set when the page was cold-launched (a shared link or label QR) with nothing beneath it. The
   * back button then becomes a labeled pill naming where it goes; absent, it is a plain Back.
   */
  cold?: { label: string; accessibilityLabel: string; };
  /** Fires on the back button; the caller decides between `back()` and `replace()`. */
  onBack: () => void;
  /** The back button's testID. */
  testID: string;
};

/**
 * The back header of a public card page, shared by a table's card detail
 * (`browse/[eventId]/[itemId]`) and a shop's card detail (`shop/[slug]/item/[itemId]`).
 */
export function PublicDetailHeader({ title, cold, onBack, testID }: PublicDetailHeaderProps) {
  const insets = useSafeAreaInsets();
  const c = useThemeColors();
  return (
    <View
      className="flex-row items-center gap-2 border-b border-border px-3 pb-3"
      style={{ paddingTop: insets.top + 12 }}
    >
      <Pressable
        testID={testID}
        onPress={onBack}
        accessibilityRole="button"
        accessibilityLabel={cold ? cold.accessibilityLabel : "Back"}
        // A 44pt frame, not `hitSlop`, which react-native-web's Pressable ignores
        // (`lib/ui/touchTarget`).
        className={cn(
          "h-11 flex-row items-center",
          cold
            ? "gap-1 rounded-full border border-border bg-surface pl-2 pr-3"
            : "w-11 justify-center",
        )}
      >
        <ChevronLeft size={cold ? 18 : 24} color={c.foreground} />
        {cold ? <Text className="font-label text-sm text-foreground">{cold.label}</Text> : null}
      </Pressable>
      <Text className="flex-1 text-base font-title text-foreground" numberOfLines={1}>
        {title}
      </Text>
    </View>
  );
}
